import { cn } from "@/lib/utils";
import { reportClientError } from "@/lib/clientErrorReporting";
import { forceFreshCurrentPage, getFreshCurrentPageUrl, isLegacyDirectoryDisplayNameError, isStaleBundleError, recoverFromStaleBundle } from "@/lib/staleBundleRecovery";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export function getErrorBoundaryCopy(error: Error | null) {
  const staleBundle = isStaleBundleError(error);
  return {
    staleBundle,
    heading: staleBundle ? "This page needs a quick refresh." : "We hit a temporary issue.",
    description: staleBundle
      ? "Releafa updates in the background. Reloading will reconnect this page to the latest version."
      : "Please reload the page and try again.",
    detail: staleBundle
      ? "Your session may be holding an older page version. Reload to continue."
      : "Your information is still safe. Reload to continue.",
  };
}

export function shouldAutoRecoverInsteadOfRendering(error: Error | null): boolean {
  return isStaleBundleError(error);
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidMount() {
    window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  componentDidCatch(error: Error, errorInfo: { componentStack?: string }) {
    reportClientError(error, "error-boundary", errorInfo.componentStack);
    if (isStaleBundleError(error)) {
      const recoveryStarted = recoverFromStaleBundle(error);
      // A prior recovery key may exist in an embedded-browser session. This is
      // the one exact retained directory reference that must still get a fresh
      // document instead of leaving the visitor on the error screen.
      if (!recoveryStarted && isLegacyDirectoryDisplayNameError(error)) forceFreshCurrentPage();
    }
  }

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    reportClientError(event.reason, "unhandled-rejection");
    if (isStaleBundleError(event.reason)) recoverFromStaleBundle(event.reason);
  };

  render() {
    if (this.state.hasError) {
      const copy = getErrorBoundaryCopy(this.state.error);
      if (shouldAutoRecoverInsteadOfRendering(this.state.error)) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-background p-8 text-center">
            <div className="flex max-w-sm flex-col items-center gap-4 text-muted-foreground">
              <p>Opening the current Releafa page…</p>
              <a
                href={getFreshCurrentPageUrl()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
              >
                Open latest version
              </a>
            </div>
          </div>
        );
      }
      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-8">
            <AlertTriangle
              size={48}
              className="text-destructive mb-6 flex-shrink-0"
            />

            <h2 className="text-xl mb-2">
              {copy.heading}
            </h2>
            <p className="text-sm text-muted-foreground text-center mb-4">
              {copy.description}
            </p>

            <div className="p-4 w-full rounded bg-muted text-sm text-muted-foreground text-center mb-6">
              {copy.detail}
            </div>

            <button
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg",
                "bg-primary text-primary-foreground",
                "hover:opacity-90 cursor-pointer"
              )}
            >
              <RotateCcw size={16} />
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
